import { z } from "zod";

export const signInSchema = z.object({
  email: z.string().trim().email({ message: "Invalid email address" }),
  password: z.string().min(6, { message: "Password must be at least 6 characters" }),
});

export const signUpSchema = z.object({
  email: z.string().trim().email({ message: "Invalid email address" }),
  password: z.string().min(6, { message: "Password must be at least 6 characters" }),
  fullName: z.string().trim().min(2, { message: "Name must be at least 2 characters" }),
  role: z.enum(["associate_dentist", "front_office", "cluster_manager", "admin"]),
});

const basePatientSchema = z.object({
  // Name fields - calculated automatically by database trigger
  first_name: z.string().trim().min(2, { message: "First name must be at least 2 characters" }).max(100, { message: "First name must be less than 100 characters" }).regex(/^[a-zA-Z\s]+$/, { message: "First name can only contain letters" }),
  last_name: z.string().trim().max(100, { message: "Last name must be less than 100 characters" }).regex(/^[a-zA-Z\s]*$/, { message: "Last name can only contain letters" }).optional().or(z.literal("")),
  name: z.string().optional(), // Auto-calculated by database trigger
  
  // Contact information - enforce exactly 10 digits for India
  phone: z.string().trim()
    .min(1, { message: "Phone number is required" })
    .refine((val) => {
      const digits = val.replace(/\D/g, '');
      const localDigits = digits.length > 10 ? digits.slice(-10) : digits;
      return localDigits.length === 10;
    }, { message: "Phone number must be exactly 10 digits" })
    .refine((val) => {
      const digits = val.replace(/\D/g, '');
      const localDigits = digits.length > 10 ? digits.slice(-10) : digits;
      return /^[6-9]\d{9}$/.test(localDigits);
    }, { message: "Invalid Indian mobile number (must start with 6-9)" }),
  alternate_phones: z.array(z.string().regex(/^[0-9+\-\s()]+$/, { message: "Please enter valid phone numbers" })).optional(),
  email: z.string().trim().min(1, { message: "Email is required" }).email({ message: "Please enter a valid email address" }),
  
  // Identification
  registration_number: z.string().trim().min(1, { message: "Registration number is required" }).max(50, { message: "Registration number must be less than 50 characters" }),
  external_id: z.string().trim().max(50, { message: "External ID must be less than 50 characters" }).optional(),
  
  // Personal details
  date_of_birth: z.string().optional(),
  age: z.number().min(0, { message: "Age must be positive" }).max(150, { message: "Age must be less than 150" }).optional(),
  gender: z.enum(["male", "female", "other"], {
    required_error: "Gender is required"
  }),
  blood_group: z.enum(["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "Unknown"]).optional(),
  marital_status: z.enum(["single", "married", "divorced", "widowed", "other"]).optional(),
  relationship: z.string().max(50, { message: "Relationship must be less than 50 characters" }).optional(),
  
  // Location and referral
  country: z.string().default("India"),
  referral_source: z.enum([
    "walk_in", 
    "doctor_referral", 
    "patient_referral", 
    "google",
    "facebook",
    "social_media", 
    "website",
    "online_appointments",
    "newspaper", 
    "hoarding", 
    "other"
  ], { required_error: "Referral source is required" }),
  
  // Guardian details
  guardian_details: z.object({
    name: z.string().max(100).optional(),
    phone: z.string().regex(/^[0-9+\-\s()]+$/).optional().or(z.literal("")),
    address: z.string().max(500).optional(),
  }).optional(),
  
  // Address details
  address: z.object({
    street: z.string().max(200).optional(),
    area: z.string().min(1, { message: "Area is required" }).max(100),
    landmark: z.string().max(100).optional(),
    city: z.string().min(1, { message: "City is required" }).max(100),
    state: z.string().min(1, { message: "State is required" }).max(100),
    zip: z.string().min(1, { message: "ZIP Code is required" }).max(20),
  }),
  
  notes: z.string().max(500, { message: "Notes must be less than 500 characters" }).optional(),
});

export const createPatientSchema = basePatientSchema
  .refine((data) => data.date_of_birth || data.age !== undefined, {
    message: "Either Date of Birth or Age is required",
    path: ["date_of_birth"],
  });

export const updatePatientSchema = basePatientSchema.partial().required({
  first_name: true,
});

export const createCaseSchema = z.object({
  patient_id: z.string().uuid({ message: "Please select a patient" }),
  workType: z.string().min(1, { message: "Work type is required" }),
  material: z.string().optional(),
  shade: z.string().optional(),
  notes: z.string().max(1000, { message: "Notes must be less than 1000 characters" }).optional(),
  promised_due_date: z.string().min(1, { message: "Promised due date is required" }),
});

export const clinicSchema = z.object({
  name: z.string().trim().min(2, { message: "Clinic name must be at least 2 characters" }),
  code: z.string().trim().min(2, { message: "Code must be at least 2 characters" }),
  email: z.string().trim().email().optional().or(z.literal("")),
  phone: z.string().trim().optional(),
  address: z.object({
    street: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postalCode: z.string().optional(),
    country: z.string().optional(),
  }).optional(),
  google_maps_link: z.string().url({ message: "Please enter a valid URL" }).optional().or(z.literal("")),
});

export const materialWarrantySchema = z.object({
  workType: z.string().min(1, { message: "Work type is required" }),
  materialName: z.string().trim().min(2, { message: "Material name is required" }),
  warrantyYears: z.number().min(0).max(99, { message: "Warranty must be between 0-99 years" }),
});

export const createTaskSchema = z.object({
  title: z.string().trim().min(2, { message: "Title is required" }),
  description: z.string().optional(),
  priority: z.enum(["low", "medium", "high", "urgent"]),
  assigned_to: z.string().uuid().optional().or(z.literal("")),
  due_date: z.string().optional(),
  case_id: z.string().uuid().optional().or(z.literal("")),
  patient_id: z.string().uuid().optional().or(z.literal("")),
  notes: z.string().optional(),
  is_recurring: z.boolean().optional(),
  recurrence_pattern: z.string().optional(),
  recurrence_end_date: z.string().optional(),
  recurrence_count: z.number().min(1).max(365).optional(),
  recurrence_end_type: z.enum(["never", "date", "count"]).optional(),
}).refine((data) => {
  if (data.is_recurring && data.recurrence_end_type !== "never") {
    return data.recurrence_end_date || data.recurrence_count;
  }
  return true;
}, {
  message: "Recurring tasks must have an end date or occurrence limit",
  path: ["recurrence_end_date"],
});

export const appointmentSchema = z.object({
  patient_id: z.string().uuid({ message: "Please select a patient" }),
  patient_name: z.string().min(1, { message: "Patient name is required" }),
  patient_phone: z.string().optional(),
  doctor_id: z.string().uuid({ message: "Please select a doctor" }),
  appointment_type_id: z.string().uuid().optional().or(z.literal("")),
  date: z.string().min(1, { message: "Date is required" }),
  start_time: z.string().min(1, { message: "Start time is required" }),
  duration_minutes: z.number().min(5, { message: "Duration must be at least 5 minutes" }).default(30),
  reason_for_visit: z.string().max(500, { message: "Reason must be less than 500 characters" }).optional(),
  notes: z.string().max(1000, { message: "Notes must be less than 1000 characters" }).optional(),
  status: z.enum(["scheduled", "confirmed", "checked_in", "in_progress", "completed", "cancelled", "no_show"]).default("scheduled"),
}).refine((data) => {
  const [startHours, startMinutes] = data.start_time.split(':').map(Number);
  const startInMinutes = startHours * 60 + startMinutes;
  const endInMinutes = startInMinutes + data.duration_minutes;
  return endInMinutes > startInMinutes && endInMinutes <= 24 * 60;
}, {
  message: "Appointment must end within the same day",
  path: ["start_time"],
});

export const inventoryItemSchema = z.object({
  name: z.string().trim().min(2, { message: "Name is required" }),
  sku: z.string().trim().optional(),
  description: z.string().optional(),
  category: z.enum(["ceramics", "metals", "tools", "consumables", "packaging", "other"]),
  unit: z.string().trim().min(1, { message: "Unit is required" }),
  current_quantity: z.number().min(0, { message: "Quantity cannot be negative" }),
  min_quantity: z.number().min(0, { message: "Min quantity cannot be negative" }),
  location: z.string().optional(),
  notes: z.string().optional(),
  unit_cost: z.coerce.number().min(0, "Unit cost cannot be negative").optional().nullable(),
});

export const addStockSchema = z.object({
  quantity: z.number().positive("Quantity must be positive"),
  batch_number: z.string().optional(),
  notes: z.string().optional(),
});

export const recordUsageSchema = z.object({
  quantity: z.number().positive("Quantity must be positive"),
  case_id: z.string().uuid().optional(),
  notes: z.string().optional(),
});

export const profileUpdateSchema = z.object({
  full_name: z.string().trim().min(2, { message: "Name must be at least 2 characters" }).max(100, { message: "Name must be less than 100 characters" }),
  phone: z.string().trim().optional(),
});

export const passwordChangeSchema = z.object({
  currentPassword: z.string().min(6, { message: "Current password is required" }),
  newPassword: z.string().min(6, { message: "New password must be at least 6 characters" }),
  confirmPassword: z.string().min(6, { message: "Please confirm your password" }),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export const recoveryPasswordSchema = z.object({
  newPassword: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});
